import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TopNav from '@/components/TopNav';
import HeroSection from '@/components/HeroSection';
import TextTranslation from '@/components/TextTranslation';
import ImageTranslation from '@/components/ImageTranslation';
import VoiceTranslation from '@/components/VoiceTranslation';
import TranslationHistory from '@/components/TranslationHistory';
import TrialProgress from '@/components/TrialProgress';
import Footer from '@/components/Footer';
import StickyMobileCTA from '@/components/StickyMobileCTA';
import TrustSection from '@/components/TrustSection';
import { useMembershipStatus } from '@/hooks/useMembershipStatus';
import { useTrialData } from '@/hooks/useTrialData';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, Type, ImageIcon, Mic } from 'lucide-react';

const Translator = () => {
  const { user, loading, signOut } = useAuth();
  const { accessState } = useMembershipStatus();
  const { remainingRequests, isSubscribed } = useTrialData();
  const translatorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const scrollToTranslator = () => {
    translatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  const isTrialUser = !!user && !isSubscribed;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/10 to-accent/10">
      <TopNav user={user} onLogout={signOut} />
      <HeroSection
        onGetStarted={scrollToTranslator}
        remainingRequests={remainingRequests}
        isTrialUser={isTrialUser}
      />

      <div ref={translatorRef} className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
        {(accessState === 'active' || accessState === 'trial') && (
          <div className="mb-4">
            <TrialProgress />
          </div>
        )}

        <Tabs defaultValue="text" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4 h-11 sm:h-10">
            <TabsTrigger value="text" className="text-xs sm:text-base py-2.5 sm:py-2 gap-1.5">
              <Type className="h-4 w-4" />
              <span className="sm:hidden">Text</span>
              <span className="hidden sm:inline">{t('translator.textTranslation')}</span>
            </TabsTrigger>
            <TabsTrigger value="image" className="text-xs sm:text-base py-2.5 sm:py-2 gap-1.5">
              <ImageIcon className="h-4 w-4" />
              <span className="sm:hidden">Image</span>
              <span className="hidden sm:inline">{t('translator.imageTranslation')}</span>
            </TabsTrigger>
            <TabsTrigger value="voice" className="text-xs sm:text-base py-2.5 sm:py-2 gap-1.5">
              <Mic className="h-4 w-4" />
              <span className="sm:hidden">Voice</span>
              <span className="hidden sm:inline">{t('translator.voiceTranslation')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="space-y-4">
            <TextTranslation accessState={accessState} />
          </TabsContent>

          <TabsContent value="image" className="space-y-4">
            <ImageTranslation accessState={accessState} />
          </TabsContent>

          <TabsContent value="voice" className="space-y-4">
            <VoiceTranslation accessState={accessState} />
          </TabsContent>
        </Tabs>

        {(accessState === 'active' || accessState === 'trial') && (
          <div className="mt-6 sm:mt-8">
            <TranslationHistory />
          </div>
        )}

        <TrustSection />

        {/* Secondary CTA */}
        <div className="mt-8 sm:mt-10 text-center py-6 sm:py-8 rounded-xl bg-primary/5 border border-primary/10">
          <p className="text-base sm:text-lg font-semibold mb-3">{t('cta.readyToTranslate')}</p>
          {!user ? (
            <Button size="lg" onClick={() => navigate('/auth')} className="text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-5">
              {t('hero.ctaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          ) : accessState === 'trial_ended' ? (
            <Button size="lg" onClick={() => navigate('/subscribe')} className="text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-5">
              {t('cta.upgradeUnlimited')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          ) : null}
        </div>
      </div>

      <Footer />
      <StickyMobileCTA />
      {/* Spacer for sticky CTA on mobile for guests */}
      {!user && <div className="h-16 md:hidden" />}
    </div>
  );
};

export default Translator;
